import { useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAppSelector } from '@/shared/store/hooks';
import toast from 'react-hot-toast';
import {
  selectIsAuthenticated,
  selectIsAuthChecking,
  selectIsAuthInitialized,
  selectAuthStatus,
  selectPermissions,
  selectPermissionsLoaded,
} from '@/shared/store/slices/authSlice';
import type { PermissionCode } from '@/features/auth/permissions';

interface ProtectedRouteProps {
  permission?: PermissionCode | PermissionCode[];
}

/**
 * Safety net for permission-gated routes. Permissions are loaded during the
 * auth check, so this should never elapse; if it does (stale persisted session,
 * failed /user/me), fall through to the permission check and redirect instead
 * of leaving the user on a spinner forever.
 */
const PERMISSION_WAIT_TIMEOUT_MS = 10000;
interface PublicRouteProps {
  children: React.ReactNode;
}
/**
 * Protected Route Wrapper with Centralized Auth State
 * Uses Redux auth state instead of direct token checking
 * Provides loading states and proper initialization handling
 */
export const ProtectedRoute = ({ permission }: ProtectedRouteProps) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isChecking = useAppSelector(selectIsAuthChecking);
  const isInitialized = useAppSelector(selectIsAuthInitialized);
  const authStatus = useAppSelector(selectAuthStatus);
  const permissions = useAppSelector(selectPermissions);
  const permissionsLoaded = useAppSelector(selectPermissionsLoaded);

  const isWaitingForPermissions = Boolean(permission) && !permissionsLoaded;
  const [permissionWaitExpired, setPermissionWaitExpired] = useState(false);

  useEffect(() => {
    if (!isWaitingForPermissions) {
      setPermissionWaitExpired(false);
      return;
    }

    const timer = setTimeout(
      () => setPermissionWaitExpired(true),
      PERMISSION_WAIT_TIMEOUT_MS
    );

    return () => clearTimeout(timer);
  }, [isWaitingForPermissions]);

  // Show loading spinner while checking auth status
  if (!isInitialized || isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        <span className="ml-4 text-lg">Authenticating...</span>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="login" replace />;
  }

  // Handle expired sessions with specific messaging (only check after auth is confirmed)
  if (authStatus === 'expired') {
    toast.error('Your session has expired. Please login again.');
    return <Navigate to="login?reason=expired" replace />;
  }

  if (isWaitingForPermissions && !permissionWaitExpired) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div
          role="status"
          aria-label="Loading permissions"
          className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"
        />
      </div>
    );
  }

  if (permission) {
    const required = Array.isArray(permission) ? permission : [permission];

    const hasAccess = required.every((p) => permissions.includes(p));

    if (!hasAccess) {
      return <Navigate to="/home" replace />;
    }
  }

  // return <>{children}</>;
  return <Outlet />;
};

/**
 * Public Route Wrapper (for login page) with Centralized Auth State
 * If user is already authenticated, redirect to home
 * Handles loading states properly
 */
export const PublicRoute = ({ children }: PublicRouteProps) => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Redirect to home if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
